/**
 * DentalHealthIntelligencePage — Phase A flagship preventive/educational module.
 *
 * Sections (Phase A — backend live):
 *   1. Oral Ecosystem Dashboard (5 zones: enamel, dentin, periodontium,
 *      microbiome, saliva)
 *   2. Oral Metabolic Intelligence (Oral Metabolic Score 0-100 + 5 sub-scores)
 *
 * Phase B will add Caries / Periodontal / Microbiome Intelligence + 3D twin.
 * Phase C will add Tooth Protection Score, Systemic Connections, Dental Age™.
 * Phase D will add Oral Cancer Prevention, Intervention Engine, Ecosystem Map.
 *
 * ⚠️ STRICT EDUCATIONAL positioning — NO diagnostic / prognostic claims.
 */
import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import {
  Smile, ShieldAlert, Sparkles, Activity, Loader2, Lock, ExternalLink, CalendarCheck,
  Ruler, Droplets, Shield, Brush, ChevronDown,
} from "lucide-react";
import { motion } from "framer-motion";
import { useAuth } from "@/App";
import { useLanguage } from "@/i18n/LanguageContext";
import { useTheme } from "@/theme/ThemeContext";
import SEO from "@/components/SEO";
import { SubpageNav, SubpageBackLink, SubpageFooter } from "@/components/SubpageLayout";
import ToothAnatomyVisual from "@/components/dental_intel/ToothAnatomyVisual";
import EducationalDeepDive from "@/components/dental_intel/EducationalDeepDive";
import InlineRiskAssessment from "@/components/dental_intel/InlineRiskAssessment";
import CariesIntelligenceCard from "@/components/dental_intel/CariesIntelligenceCard";
import PeriodontalIntelligenceCard from "@/components/dental_intel/PeriodontalIntelligenceCard";
import MicrobiomeIntelligenceCard from "@/components/dental_intel/MicrobiomeIntelligenceCard";
import ModuleRatingButton from "@/components/ModuleRatingButton";  // iter1114.b
import ToothProtectionScoreCard from "@/components/dental_intel/ToothProtectionScoreCard";
import SystemicPathwaysCard from "@/components/dental_intel/SystemicPathwaysCard";
import SystemicSafetyCard from "@/components/dental_intel/SystemicSafetyCard";
import PediatricOralCard from "@/components/dental_intel/PediatricOralCard";
import OralHabitsCard from "@/components/dental_intel/OralHabitsCard";
import DentalAgeCard from "@/components/dental_intel/DentalAgeCard";
import OralCancerPreventionCard from "@/components/dental_intel/OralCancerPreventionCard";
import InterventionEngineCard from "@/components/dental_intel/InterventionEngineCard";
import OralEcosystemMap from "@/components/dental_intel/OralEcosystemMap";
import FutureModulesPreview from "@/components/dental_intel/FutureModulesPreview";
import SpecialisedSubModuleSection from "@/components/dental_intel/SpecialisedSubModuleSection";
import ScoreExplanation from "@/components/dental_intel/ScoreExplanation";
import SaveToHealthIntelligenceButton from "@/components/SaveToHealthIntelligenceButton";
import ComputeIndexCTA from "@/components/ComputeIndexCTA";
import BhiQuickNav from "@/components/breast_intel/BhiQuickNav";
import DentalPlanCard from "@/components/dental_intel/DentalPlanCard";
import DentalDiaryCard from "@/components/dental_intel/DentalDiaryCard";
import OralIndicesCard from "@/components/dental_intel/OralIndicesCard";
import FluorideIntelligenceCard from "@/components/dental_intel/FluorideIntelligenceCard";
import SealantTrackerCard from "@/components/dental_intel/SealantTrackerCard";
import OralSelfExamCard from "@/components/dental_intel/OralSelfExamCard";
import OralDeficiencySignsCard from "@/components/dental_intel/OralDeficiencySignsCard";  // iter1033
// iter1111 — new factor-summary cards that render after the composite-score retirement
import {
  OralEcosystemFactorCard,
  OralMetabolicFactorCard,
  ToothProtectionFactorCard,
  MicrobiomeFactorCard,
  ImplantReadinessFactorCard,
  DentalAgeFactorCard,
  ForecastFactorCard,
  SystemicPathwaysFactorCard,
} from "@/components/dental_intel/DhiRetiredCards";
import BrushingTechniqueCard from "@/components/dental_intel/BrushingTechniqueCard";
import ImplantHealthCard from "@/components/dental_intel/ImplantHealthCard";
import FamilySubjectPicker from "@/components/dental_intel/FamilySubjectPicker";
import ScoreMethodology, { BandLine } from "@/components/dental_intel/ScoreMethodology";
import ScoreForecast from "@/components/dental_intel/ScoreForecast";
import PhysiologyBridgeCard from "@/components/PhysiologyBridgeCard";
import DentalPriorityCard from "@/components/dental_intel/DentalPriorityCard";
import ProfileContextStrip from "@/components/shared/ProfileContextStrip";
import { useBhiLayout, BhiSectionOrderPanel, BhiOrderContext, useBhiOrderStyle } from "@/components/breast_intel/BhiLayoutOrder";

const TX = {
  bg: {
    title: "Здраве на зъбите и устата",
    subtitle: "Образователен огледален образ на устното здраве — пет орални среди, метаболитна оценка и лични мерки",
    description: "Образователен и превантивен инструмент за метаболитното здраве на устната кухина. НЕ е диагноза и не заменя зъболекаря.",
    back: "Здравна оценка",
    loginCta: "Влезте за достъп до модула",
    loginCtaText: "Денталното ми здраве изисква защитен потребителски акаунт за опазване на здравните данни.",
    loginBtn: "Вход",
    registerBtn: "Регистрация",
    sections: {
      ecosystem: "Орална екосистема",
      metabolic: "Орална метаболитна оценка",
      risk:      "Дентална оценка на риска",
      indices:   "Орални индекси",
      fluoride:  "Флуор и защита на емайла",
      sealants:  "Силанти на ями и фисури",
      brushing:  "Техника на четкане",
      selfexam:  "Самоизследване на устната кухина",
      physiology: "Физиологичен мост",
      caries:    "Кариес — образователна среда",
      periodontal: "Пародонтално възпаление",
      microbiome:  "Орален микробиом",
      protection:  "Оценка за защита на зъба",
      pathways:    "Системно-орални пътища",
      dental_age:  "Дентална възраст",
      cancer:      "Превенция на орален рак",
      intervention: "Персонализирани препоръки",
      map:         "Образователна екосистемна карта",
      pediatric:   "Педиатрична орална превенция",
      orthodontic: "Орални навици и лицево развитие",
      implant:     "Здраве на импланта",
    },
    ecosystemHelp: "Пет среди — образователна оценка на хранителните, метаболитни и възпалителни фактори, които ги влияят. НЕ е тъканно измерване.",
    metabolicHelp: "Обща орална метаболитна оценка (0–100) от пет под-среди: захарна експозиция, киселина, възпаление, инсулинова среда, защитна храна.",
    statusBands: { green: "Защитен", yellow: "Среден", orange: "Внимание", red: "Висок натиск" },
    selectZone: "Изберете зона за подробности",
    refresh: "Обнови данните",
    factors: "Фактори",
    age: "Възраст",
    bmi: "BMI",
    nis_avg: "Среден NIS",
    dil_avg: "Среден DIL",
    sugar: "Захар/ден",
    fiber: "Фибри/ден",
    meals: "хранения",
    last_14: "последни 14 дни",
    screenLink: "Към денталния скрининг (CAMBRA + EFP 2018) →",
    bookConsultant: "Консултация с д-р Лили Иванова — дентален консултант на Nutramilo",
    bookConsultantDesc: "Дентален консултант на Nutramilo. Запазете час директно в SuperDoc. Nutramilo НЕ е медицинска платформа.",
    disclaimerTitle: "Научен и медицински статус",
    phaseNote: "12 свързани секции — образователен дигитален близнак. Никога не е диагноза.",
  },
  en: {
    title: "Dental Health Intelligence",
    subtitle: "An educational digital twin of oral health — five oral environments, metabolic intelligence, and personalised interventions",
    description: "Educational and preventive tool. NOT intended for diagnosis, treatment, or prediction of dental disease.",
    back: "Health overview",
    loginCta: "Sign in to access the module",
    loginCtaText: "My Dental Health requires a secured user account to protect health data.",
    loginBtn: "Sign in",
    registerBtn: "Register",
    sections: {
      ecosystem: "Oral ecosystem",
      metabolic: "Oral metabolic intelligence",
      risk:      "Dental risk assessment",
      indices:   "Oral indices lab",
      fluoride:  "Fluoride intelligence",
      sealants:  "Pit and fissure sealants",
      brushing:  "Brushing technique",
      selfexam:  "Oral self-examination",
      physiology: "Physiology bridge",
      caries:    "Caries — educational environment",
      periodontal: "Periodontal inflammation",
      microbiome:  "Oral microbiome",
      protection:  "Tooth Protection Score",
      pathways:    "Systemic-oral pathways",
      dental_age:  "Dental age",
      cancer:      "Oral cancer prevention",
      intervention: "Intervention Engine",
      map:         "Ecosystem map",
      pediatric:   "Pediatric oral health",
      orthodontic: "Oral habits and orofacial development",
      implant:     "Implant health",
    },
    ecosystemHelp: "Five environments — an educational index of the nutritional, metabolic and inflammatory factors that influence each. NOT a tissue measurement.",
    metabolicHelp: "Composite Oral Metabolic Score (0–100) from five sub-environments: sugar exposure, acid, inflammation, insulin environment, protective nutrition.",
    statusBands: { green: "Protective", yellow: "Average", orange: "Caution", red: "High burden" },
    selectZone: "Select a zone for details",
    refresh: "Refresh data",
    factors: "Factors",
    age: "Age",
    bmi: "BMI",
    nis_avg: "Avg NIS",
    dil_avg: "Avg DIL",
    sugar: "Sugar/day",
    fiber: "Fiber/day",
    meals: "meals",
    last_14: "last 14 days",
    screenLink: "Go to dental screening (CAMBRA + EFP 2018) →",
    bookConsultant: "Book Dr. Lili Ivanova — Nutramilo dental consultant",
    bookConsultantDesc: "Nutramilo dental consultant. Book directly on SuperDoc. Nutramilo is NOT a medical platform.",
    disclaimerTitle: "Scientific & medical status",
    phaseNote: "Educational digital twin with 12 connected sections. NEVER a diagnosis.",
  },
};

const BAND_PILL = {
  green:  "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-300",
  yellow: "bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-300",
  orange: "bg-orange-100 text-orange-700 dark:bg-orange-500/20 dark:text-orange-300",
  red:    "bg-red-100 text-red-700 dark:bg-red-500/20 dark:text-red-300",
};

// iter1111 — Backward-compat shim for the backend score retirement audit.
// The backend now returns `status: "retired_unvalidated"` (or similar) for
// several composite sections. The legacy render paths in this file expect
// numeric `.total`, `.score`, `.band`. This normaliser injects safe
// null-equivalent values so the UI does not crash, while flagging each
// retired section with `_retired: true` so cards can show an explanatory
// "N/A — retired" badge instead of a fake number.
function _retiredPlaceholder(baseName) {
  return {
    _retired: true,
    retired_name: baseName,
    name_bg: "Оттеглено",
    name_en: "Retired",
    band: "gray",
    score: null,
    total: null,
    factors: [],
  };
}
function _normaliseDhiSnapshot(data) {
  if (!data || typeof data !== "object") return data;
  const out = { ...data };
  const isRetired = (s) =>
    s && typeof s === "object" &&
    (String(s.status || "").startsWith("retired_") ||
     s.status === "checklist" ||
     s.status === "factor_summary" ||
     s.status === "educational_associations" ||
     s.status === "clinician_checklist");

  // oral_metabolic — needs `.total` + `.band` for the hero display.
  if (isRetired(out.oral_metabolic)) {
    out.oral_metabolic = {
      ...out.oral_metabolic,
      _retired: true,
      total: null,
      band: "gray",
      components: out.oral_metabolic.components_as_factors || {},
    };
  }
  // oral_ecosystem — 5 zones each expecting `.score` + `.band` + `.name_*`.
  if (isRetired(out.oral_ecosystem)) {
    const retired = {
      _retired: true,
      retired_name: out.oral_ecosystem.retired_name,
      factors: out.oral_ecosystem.factors || [],
    };
    out.oral_ecosystem = {
      ...retired,
      enamel: { ..._retiredPlaceholder("enamel"), name_bg: "Емайл", name_en: "Enamel" },
      dentin: { ..._retiredPlaceholder("dentin"), name_bg: "Дентин", name_en: "Dentin" },
      periodontium: { ..._retiredPlaceholder("periodontium"), name_bg: "Пародонт", name_en: "Periodontium" },
      microbiome: { ..._retiredPlaceholder("microbiome"), name_bg: "Микробиом", name_en: "Microbiome" },
      saliva: { ..._retiredPlaceholder("saliva"), name_bg: "Слюнка", name_en: "Saliva" },
    };
  }
  // tooth_protection, microbiome_intelligence, implant_health,
  // dental_age, forecast, systemic_pathways — mark retired.
  for (const key of ["tooth_protection", "microbiome_intelligence",
                     "implant_health", "dental_age", "forecast",
                     "systemic_pathways"]) {
    if (isRetired(out[key])) {
      out[key] = {
        ...out[key],
        _retired: true,
        score: out[key].score ?? null,
        band: out[key].band ?? "gray",
      };
    }
  }
  return out;
}

export default function DentalHealthIntelligencePage() {
  const { user, API, loading: authLoading } = useAuth();
  const { language } = useLanguage();
  const { isDark } = useTheme();
  const lang = language === "en" ? "en" : language === "es" ? "es" : "bg";
  const t = TX[lang] || TX.bg;

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState(null);
  const [selectedZone, setSelectedZone] = useState("enamel");
  const [childId, setChildId] = useState(null);
  const collapse = useDhiCollapse();  // iter1029 — сгъваеми секции
  // iter1018 — ред на модулите (админ) + прост/разширен изглед, както в BHI
  const { layout, orderMap, setLayout } = useBhiLayout("dhi");
  const ord = (id) => (orderMap ? { order: orderMap[id] ?? 9990 } : undefined);
  const [tier, setTier] = useState(() =>
    localStorage.getItem("dhi:tier") || "essential");
  const setTierAnd = (v) => { setTier(v); localStorage.setItem("dhi:tier", v); };

  const fetchSnapshot = useCallback(async () => {
    setLoading(true);
    setErr(null);
    try {
      const { data } = await axios.get(`${API}/dental-intelligence/snapshot`);
      // iter1111 — backend retired several composite scores. The response
      // may now contain `status: "retired_unvalidated"` sections with no
      // `score` / `total` / `band`. Normalise to safe shapes so the
      // legacy render paths below don't crash. Retired sections show
      // an "N/A — retired" badge instead of a fake number.
      setData(_normaliseDhiSnapshot(data));
    } catch (e) {
      setErr(e?.response?.data?.detail || e.message);
    } finally {
      setLoading(false);
    }
  }, [API]);

  useEffect(() => {
    if (!authLoading && user) {
      fetchSnapshot();
    }
  }, [authLoading, user, fetchSnapshot]);

  // iter582 — Real-time Plate → DHI loop. When the user logs a meal on the
  // Plate Simulator, PlateSimulator dispatches `nutramilo:meals:updated`.
  // We listen here and refresh the snapshot so the user instantly sees
  // changes in Sugar Exposure + Microbiome Balance scores.
  //
  // iter1110 — also listen for `nutramilo:dental:updated` so dental diary
  // check-ins and self-exam submits refresh the whole DHI snapshot.
  useEffect(() => {
    if (!user) return undefined;
    let timer = null;
    const onUpdated = () => {
      // small debounce so rapid changes don't hammer the API
      if (timer) clearTimeout(timer);
      timer = setTimeout(() => { fetchSnapshot(); }, 800);
    };
    window.addEventListener("nutramilo:meals:updated", onUpdated);
    window.addEventListener("nutramilo:dental:updated", onUpdated);
    return () => {
      window.removeEventListener("nutramilo:meals:updated", onUpdated);
      window.removeEventListener("nutramilo:dental:updated", onUpdated);
      if (timer) clearTimeout(timer);
    };
  }, [user, fetchSnapshot]);

  // Auth gate
  if (!authLoading && !user) {
    return (
      <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-[#1A1A2E]"}`}>
        <SEO title={t.title} description={t.description} path="/health-intelligence/dental" />
        <SubpageNav />
        <div className="max-w-3xl mx-auto px-4 py-12 text-center">
          <Lock className="w-12 h-12 mx-auto opacity-50 mb-4" />
          <h1 className="text-2xl font-bold mb-2">{t.loginCta}</h1>
          <p className="opacity-70 mb-6">{t.loginCtaText}</p>
          <div className="flex flex-wrap justify-center gap-3">
            <Link to="/login" className="px-5 py-2.5 rounded-lg bg-cyan-600 text-white font-semibold" data-testid="dhi-login-btn">
              {t.loginBtn}
            </Link>
            <Link to="/register" className="px-5 py-2.5 rounded-lg bg-gray-900 text-white font-semibold" data-testid="dhi-register-btn">
              {t.registerBtn}
            </Link>
          </div>
        </div>
        <SubpageFooter />
      </div>
    );
  }

  const cardCls = `rounded-2xl border ${
    isDark ? "bg-[hsl(222,47%,11%)] border-[hsl(223,47%,18%)]" : "bg-white border-gray-200"
  }`;

  return (
    <div className={`min-h-screen ${isDark ? "bg-[hsl(222,47%,6%)] text-white" : "bg-[#F8FAFC] text-[#1A1A2E]"}`}>
      <SEO title={t.title} description={t.description} path="/health-intelligence/dental" />
      <SubpageNav />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        <SubpageBackLink to="/dashboard/health-intelligence" label={t.back} />

        <BhiQuickNav isDark={isDark} lang={lang} activeRoute="/health-intelligence/dental" />

        {/* Hero */}
        <header className="mt-4 mb-6">
          <div className="flex items-start gap-4">
            <div className={`w-14 h-14 rounded-2xl flex items-center justify-center shrink-0 ${
              isDark ? "bg-cyan-500/20" : "bg-cyan-100"
            }`}>
              <Smile className={`w-7 h-7 ${isDark ? "text-cyan-300" : "text-cyan-600"}`} />
            </div>
            <div className="flex-1">
              <h1 className="text-3xl sm:text-4xl font-extrabold leading-tight bg-gradient-to-r from-cyan-500 via-sky-500 to-indigo-500 bg-clip-text text-transparent">
                {t.title}
              </h1>
              <p className={`text-sm sm:text-base mt-2 ${isDark ? "text-gray-300" : "text-gray-700"}`}>
                {t.subtitle}
              </p>
            </div>
          </div>
        </header>

        {/* Изгледът — основно или разширено — стои най-отгоре */}
        <div className="flex items-center gap-2 mb-5" data-testid="dhi-tier-tabs">
          {["essential", "advanced"].map((v) => (
            <button key={v} onClick={() => setTierAnd(v)}
              className={`px-3.5 py-2 rounded-xl text-[11px] font-bold transition-all ${
                tier === v ? "bg-gradient-to-r from-cyan-500 to-sky-600 text-white shadow-md"
                           : (isDark ? "bg-white/5 hover:bg-white/10" : "bg-white border border-gray-200 hover:bg-slate-50")}`}
              data-testid={`dhi-tier-${v}`}>
              {v === "essential" ? "Основно" : "Разширено"}
            </button>
          ))}
        </div>

        {/* Disclaimer banner */}
        <div
          className={`mb-6 p-4 rounded-xl border-l-4 ${
            isDark ? "bg-amber-500/10 border-amber-500" : "bg-amber-50 border-amber-500"
          }`}
          data-testid="dhi-disclaimer-banner"
        >
          <div className="flex items-start gap-3">
            <ShieldAlert className={`w-5 h-5 shrink-0 mt-0.5 ${isDark ? "text-amber-300" : "text-amber-600"}`} />
            <div>
              <div className={`text-xs font-bold mb-1 ${isDark ? "text-amber-300" : "text-amber-700"}`}>
                {t.disclaimerTitle}
              </div>
              <p className={`text-xs leading-relaxed ${isDark ? "text-amber-200" : "text-amber-700"}`}>
                {t.description}
              </p>
              <p className={`text-[10px] mt-1.5 opacity-70 ${isDark ? "text-amber-200" : "text-amber-700"}`}>
                {t.phaseNote}
              </p>
            </div>
          </div>
        </div>

        {/* Loading / error */}
        {loading && (
          <div className="text-center py-16">
            <Loader2 className="w-8 h-8 animate-spin mx-auto opacity-50" />
            <p className="mt-3 text-sm opacity-70">{lang === "en" ? "Loading…" : lang === "es" ? "Cargando…" : "Зареждане…"}</p>
          </div>
        )}
        {err && (
          <div className="p-4 rounded-lg bg-red-500/10 text-red-500 text-sm" data-testid="dhi-error">
            {err}
          </div>
        )}

        {data && !loading && (
          <BhiOrderContext.Provider value={orderMap}>
          <DhiCollapseContext.Provider value={collapse}>
          <div className={orderMap ? "flex flex-col" : ""}>
            {/* iter1018 — денталният модул следва скелета на BHI */}
            {user?.role === "admin" && (
              <BhiSectionOrderPanel isDark={isDark} layout={layout} setLayout={setLayout}
                module="dhi" style={orderMap ? { order: -1000 } : undefined} />
            )}
            <div style={ord("dhi-priority")}>
              <DentalPriorityCard isDark={isDark} />
            </div>
            <div style={ord("dhi-profile-context")} className="mb-4">
              <ProfileContextStrip domain="dental" isDark={isDark} />
            </div>
            <Section tier={tier} icon={CalendarCheck} title="Моят план при зъболекар"
              help="Кога е следващият преглед, почистване, снимка и флуорид според категорията Ви риск."
              testid="dhi-section-plan">
              <DentalPlanCard isDark={isDark} />
              <div className="mt-3">
                <FamilySubjectPicker value={childId} onChange={setChildId} isDark={isDark} />
                <DentalDiaryCard isDark={isDark} cardCls={cardCls} childId={childId} />
              </div>
            </Section>

            {/* Section TOC — clickable anchors so all 12 sections feel connected */}
            <div className={`mb-5 p-3 rounded-xl ${isDark ? "bg-white/[0.03] border border-white/10" : "bg-white border border-gray-200"}`} data-testid="dhi-section-toc">
              <div className="text-[10px] uppercase tracking-wider font-bold opacity-50 mb-2">
                {lang === "en" ? "Connected sections" : "Свързани секции"}
              </div>
              <div className="flex flex-wrap gap-1.5">
                {Object.entries(t.sections)
                  .filter(([key]) => {
                    // iter1110 — in essential tier the full section list
                    // contained 13 anchors pointing to sections that are
                    // not rendered (dead links). Hide them so the user
                    // never clicks into nothing.
                    if (tier !== "essential") return true;
                    const id = `dhi-section-${key === "dental_age" ? "dental-age" : key}`;
                    return DHI_ESSENTIAL.has(id);
                  })
                  .sort((a, b) => {
                    const id = (k) => `dhi-section-${k === "dental_age" ? "dental-age" : k}`;
                    return (orderMap?.[id(a[0])] ?? 9990) - (orderMap?.[id(b[0])] ?? 9990);
                  })
                  .map(([key, label]) => (
                  <a
                    key={key}
                    href={`#dhi-section-${key === "dental_age" ? "dental-age" : key}`}
                    onClick={() => collapse.expand(
                      `dhi-section-${key === "dental_age" ? "dental-age" : key}`)}
                    className={`text-[11px] font-semibold px-2.5 py-1 rounded-full transition-all ${
                      isDark ? "bg-cyan-500/10 text-cyan-200 hover:bg-cyan-500/20" : "bg-cyan-50 text-cyan-700 hover:bg-cyan-100"
                    }`}
                    data-testid={`dhi-toc-${key}`}
                  >
                    {label}
                  </a>
                ))}
              </div>
              {/* iter1029 — свиване и разгъване на всички секции */}
              <div className="flex gap-2 mt-2">
                <button type="button"
                  onClick={() => collapse.collapseAll(
                    Object.keys(t.sections).map((k) =>
                      `dhi-section-${k === "dental_age" ? "dental-age" : k}`))}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-full border border-current opacity-70 hover:opacity-100"
                  data-testid="dhi-collapse-all">
                  {lang === "en" ? "Collapse all" : "Свий всички"}
                </button>
                <button type="button"
                  onClick={collapse.expandAll}
                  className="text-[11px] font-bold px-2.5 py-1 rounded-full border border-current opacity-70 hover:opacity-100"
                  data-testid="dhi-expand-all">
                  {lang === "en" ? "Expand all" : "Разгъни всички"}
                </button>
                <span className="text-[10px] opacity-55 self-center">
                  {lang === "en"
                    ? "Click a section title to fold it."
                    : "Кликнете върху заглавие, за да свиете секцията."}
                </span>
              </div>
            </div>

            {/* Top stats row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
              <StatTile testid="dhi-stat-meals"  label={`${t.meals} (${t.last_14})`} value={data.signals.meals_last_14d}
                computeRoute="/dashboard/diary" computeLabel={lang === "en" ? "Log a meal" : "Логни ястие"} lang={lang} isDark={isDark} />
              <StatTile testid="dhi-stat-nis"    label={t.nis_avg}                   value={data.signals.nis_avg ?? "—"}
                computeRoute="/dashboard/diary" computeLabel={lang === "en" ? "Compute NIS" : "Изчисли NIS"} lang={lang} isDark={isDark} />
              <StatTile testid="dhi-stat-dil"    label={t.dil_avg}                   value={data.signals.dil_avg ?? "—"}
                computeRoute="/dashboard/diary" computeLabel={lang === "en" ? "Compute DIL" : "Изчисли DIL"} lang={lang} isDark={isDark} />
              <StatTile testid="dhi-stat-sugar"  label={`${t.sugar} (g)`}            value={data.signals.sugar_per_day ?? "—"}
                computeRoute="/dashboard/diary" computeLabel={lang === "en" ? "Log foods" : "Логни храна"} lang={lang} isDark={isDark} />
            </div>

            {/* iter545 — Save snapshot of current dental state to Health Intelligence */}
            <div className="mb-6">
              <SaveToHealthIntelligenceButton
                snapshotType="digital_twin"
                title={lang === "en"
                  ? `Dental Health Snapshot — ${data.oral_metabolic?.total ?? "N/A"}/100`
                  : `Снимка на денталното здраве — ${data.oral_metabolic?.total ?? "N/A"}/100`}
                summary={lang === "en"
                  ? `Oral Metabolic ${data.oral_metabolic?.total ?? "N/A"}/100 · Caries ${data.caries_intelligence?.score ?? "N/A"}/100 · Perio ${data.periodontal_intelligence?.score ?? "N/A"}/100 · Microbiome ${data.microbiome_intelligence?.score ?? "N/A"}/100`
                  : `Орална метаболитна ${data.oral_metabolic?.total ?? "N/A"}/100 · Кариес ${data.caries_intelligence?.score ?? "N/A"}/100 · Перио ${data.periodontal_intelligence?.score ?? "N/A"}/100 · Микробиом ${data.microbiome_intelligence?.score ?? "N/A"}/100`}
                payload={{
                  source: "dental_health_intelligence",
                  module: "dental",
                  oral_metabolic: data.oral_metabolic,
                  oral_ecosystem: data.oral_ecosystem,
                  caries: data.caries_intelligence,
                  periodontal: data.periodontal_intelligence,
                  microbiome: data.microbiome_intelligence,
                  tooth_protection: data.tooth_protection,
                  dental_age: data.dental_age,
                  signals: data.signals,
                }}
                tags={["dental", "oral-health", data.oral_metabolic?.band].filter(Boolean)}
              />
            </div>

            {/* ── SECTION 1: Oral Ecosystem Dashboard ─────────── */}

            <Section tier={tier} icon={Sparkles} title={t.sections.ecosystem} help={t.ecosystemHelp} testid="dhi-section-ecosystem">
              {data.oral_ecosystem?._retired ? (
                <OralEcosystemFactorCard data={data.oral_ecosystem} lang={lang} isDark={isDark} />
              ) : (
              <>
              <div className="grid lg:grid-cols-5 gap-5">
                <div className={`${cardCls} p-5 lg:col-span-3 relative overflow-hidden`}>
                  <div className="absolute top-0 right-0 w-80 h-80 bg-cyan-500/10 rounded-full blur-3xl pointer-events-none" />
                  <ToothAnatomyVisual
                    ecosystem={data.oral_ecosystem}
                    onSelect={setSelectedZone}
                    selected={selectedZone}
                    lang={lang}
                  />
                </div>
                <div className="lg:col-span-2 space-y-3">
                  {/* Quick zone selector pills — always-clickable fallback */}
                  <div className="flex flex-wrap gap-1.5" data-testid="dhi-zone-selector">
                    {["enamel","dentin","periodontium","microbiome","saliva"].map((zk) => {
                      const z = data.oral_ecosystem[zk] || {};
                      const active = selectedZone === zk;
                      const label = lang === "en" ? z.name_en : z.name_bg;
                      return (
                        <button
                          key={zk}
                          type="button"
                          onClick={() => setSelectedZone(zk)}
                          data-testid={`dhi-zone-pill-${zk}`}
                          className={`px-2.5 py-1 rounded-full text-[11px] font-semibold transition-all ${
                            active
                              ? `ring-2 ring-offset-1 ${isDark ? "ring-offset-[hsl(222,47%,11%)]" : "ring-offset-white"} ` + BAND_PILL[z.band]
                              : (isDark ? "bg-white/5 text-white/70 hover:bg-white/10" : "bg-gray-100 text-gray-600 hover:bg-gray-200")
                          }`}
                        >
                          {label} · {z.score}
                        </button>
                      );
                    })}
                  </div>
                  <ZoneDetails
                    zone={selectedZone}
                    ecosystem={data.oral_ecosystem}
                    t={t}
                    cardCls={cardCls}
                    lang={lang}
                    isDark={isDark}
                  />
                  <p className={`text-xs italic ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {t.selectZone}
                  </p>
                </div>
              </div>

              {/* Embedded Educational Deep-Dive (anatomy + developmental defects) */}
              <div className="mt-4" data-testid="dhi-ecosystem-deepdive">
                <EducationalDeepDive isDark={isDark} lang={lang} cardCls={cardCls} />
              </div>
              </>
              )}
            </Section>

            {/* ── SECTION 2: Oral Metabolic Intelligence ────── */}
            <Section tier={tier} icon={Activity} title={t.sections.metabolic} help={t.metabolicHelp} testid="dhi-section-metabolic">
              {data.oral_metabolic?._retired ? (
                <OralMetabolicFactorCard data={data.oral_metabolic} lang={lang} isDark={isDark} />
              ) : (
              <>
              <div className="grid md:grid-cols-3 gap-5">
                <div className={`${cardCls} p-6 flex flex-col items-center`}>
                  <ScoreRing score={data.oral_metabolic.total} band={data.oral_metabolic.band} />
                  <div className="mt-3 text-sm font-semibold">
                    {lang === "en" ? "Oral Metabolic Score" : "Орална метаболитна оценка"}
                  </div>
                  <div className={`text-xs mt-1 ${isDark ? "text-gray-400" : "text-gray-500"}`}>
                    {t.statusBands[data.oral_metabolic.band]}
                  </div>
                  <ScoreExplanation
                    isDark={isDark}
                    lang={lang}
                    testid="dhi-metabolic-why"
                    inputs={lang === "en"
                      ? "Diet (DIL, fiber, sugar, meal frequency), HbA1c, HOMA-IR, hs-CRP, smoking, alcohol, omega-3 index, vitamin D, exercise."
                      : "Хранене (DIL, фибри, захари, честота на хранене), HbA1c, HOMA-IR, hs-CRP, тютюнопушене, алкохол, омега-3 индекс, vit D, физическа активност."}
                    formula={lang === "en"
                      ? "Weighted average of 5 sub-scores: Sugar 25%, Acid 15%, Inflammation 25%, Insulin 20%, Protective nutrition 15%. Each is 0-100 with bigger penalties at clinical thresholds (WHO sugar 25g, HbA1c 5.7/6.5, hs-CRP 3 mg/L)."
                      : "Претеглена сума на 5 под-оценки: Захар 25%, Киселина 15%, Възпаление 25%, Инсулин 20%, Защитна храна 15%. Всяка е 0-100, с по-големи отстъпки при клинично установени прагове (WHO захар 25g, HbA1c 5.7/6.5, hs-CRP 3 mg/L)."}
                    factors={Object.entries(data.oral_metabolic.components).map(([k, v]) => {
                      const labels = lang === "en"
                        ? { sugar: "Sugar", acid: "Acid", inflammatory: "Inflammation", insulin: "Insulin", protective: "Protective nutrition" }
                        : { sugar: "Захар", acid: "Киселина", inflammatory: "Възпаление", insulin: "Инсулин", protective: "Защитна храна" };
                      return `${labels[k] || k}: ${v.score}/100 (${t.statusBands[v.band]})`;
                    })}
                  />
                </div>
                <div className="md:col-span-2 grid grid-cols-2 sm:grid-cols-5 gap-2">
                  {Object.entries(data.oral_metabolic.components).map(([k, v]) => (
                    <SubScoreCard key={k} name={k} score={v.score} band={v.band} lang={lang} />
                  ))}
                </div>
              </div>
              {/* Единна методика + прогноза (iter1028) */}
              <div className={`${cardCls} p-5 mt-4`} data-testid="dhi-metabolic-methodology">
                <h3 className="text-sm font-bold">Как се изчислява Оралната метаболитна оценка</h3>
                <BandLine explain={data.oral_metabolic.explain} band={data.oral_metabolic.band}
                  testid="dhi-metabolic-band" />
                <p className="text-xs opacity-70 leading-snug mt-1">
                  {data.oral_metabolic.explain?.how_bg}
                </p>
                <ScoreMethodology
                  explain={data.oral_metabolic.explain}
                  contributors={data.oral_metabolic.contributors}
                  isDark={isDark}
                  title="Виж скалата, входовете и тежестите"
                  testid="dhi-metabolic-method"
                />
                <ScoreForecast forecast={data.forecast} metric="metabolic" isDark={isDark}
                  testid="dhi-metabolic-forecast" />
              </div>
              </>
              )}
              {/* iter1111 — Educational scenarios (replaces 'years gained' forecast) */}
              {data.forecast?._retired && (
                <div className="mt-4">
                  <ForecastFactorCard data={data.forecast} lang={lang} isDark={isDark} />
                </div>
              )}
            </Section>

            {/* ── SECTION 3: Inline Risk Assessment (CAMBRA + EFP) ───── */}
            <Section tier={tier} icon={Activity} title={t.sections.risk} testid="dhi-section-risk">
              <InlineRiskAssessment
                isDark={isDark}
                lang={lang}
                cardCls={cardCls}
                onSaved={() => fetchSnapshot()}
              />
            </Section>

            {/* ── Орални индекси — DMFT, OHI-S, PlI, GI, CPI, BEWE ─── */}
            <Section tier={tier} icon={Ruler} title={t.sections.indices}
              help="Класическите индекси от превантивната дентална медицина — въведени от Вас или диктувани от зъболекаря на прегледа."
              testid="dhi-section-indices">
              <OralIndicesCard isDark={isDark} cardCls={cardCls} childId={childId} />
            </Section>

            {/* ── SECTION 4: Caries Intelligence ────────────── */}
            <Section tier={tier} icon={Sparkles} title={t.sections.caries} testid="dhi-section-caries">
              <CariesIntelligenceCard
                data={data.caries_intelligence}
                lastScreening={data.last_screening}
                isDark={isDark}
                lang={lang}
                cardCls={cardCls}
              />
              <Link to="/dashboard/diary"
                className="mt-2 inline-flex items-center gap-1 text-[11px] font-bold text-cyan-600"
                data-testid="dhi-to-dental-plate">
                Дентална проверка на чинията — захарни епизоди и киселинност
              </Link>

            </Section>

            {/* ── Consultant CTA — Dr. Lili Ivanova (positioned between Caries & Periodontal per user request) ─────────── */}
            <div className={`mb-6 rounded-2xl p-4 ${isDark ? "bg-sky-900/20 border border-sky-500/30" : "bg-sky-50 border border-sky-200"}`}>
              <a
                href="https://superdoc.bg/lekar/lili-ivanova"
                target="_blank" rel="noopener noreferrer"
                className="flex items-center gap-4"
                data-testid="dhi-consultant-link"
              >
                <img
                  src="https://customer-assets.emergentagent.com/job_endocrine-hub-2/artifacts/bd1fzt0w_nBpP1acsahEZnbquQ0VZQFFaTiKZSHA1SDq4SfLN.jpg"
                  alt="Д-р Лили Иванова"
                  className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl object-cover shrink-0 ring-2 ring-sky-300/40"
                  data-testid="dhi-consultant-photo"
                  loading="lazy"
                />
                <div className="flex-1 min-w-0">
                  <div className="text-sm sm:text-base font-bold flex items-center gap-1.5">
                    {t.bookConsultant} <ExternalLink className="w-3.5 h-3.5 opacity-60" />
                  </div>
                  <div className="text-xs opacity-70 leading-snug mt-1">
                    {t.bookConsultantDesc}
                  </div>
                </div>
              </a>
            </div>

            {/* ── SECTION 5: Periodontal Intelligence ───────── */}
            <Section tier={tier} icon={Activity} title={t.sections.periodontal} testid="dhi-section-periodontal">
              <PeriodontalIntelligenceCard
                data={data.periodontal_intelligence}
                isDark={isDark}
                lang={lang}
                cardCls={cardCls}
              />
            </Section>

            {/* ── SECTION 6: Microbiome Intelligence (+ прецизен слой) ─ */}
            <Section tier={tier} icon={Sparkles} title={t.sections.microbiome} testid="dhi-section-microbiome">
              {data.microbiome_intelligence?._retired ? (
                <MicrobiomeFactorCard data={data.microbiome_intelligence} lang={lang} isDark={isDark} />
              ) : (
                <>
                  <MicrobiomeIntelligenceCard
                    data={data.microbiome_intelligence}
                    isDark={isDark}
                    lang={lang}
                    cardCls={cardCls}
                  />
                  {data.microbiome_intelligence?.precision && (
                    <details className={`mt-3 p-3 rounded-2xl border ${isDark ? "border-white/10" : "border-gray-200"}`}
                      data-testid="dhi-microbiome-precision">
                      <summary className="text-[11px] font-bold cursor-pointer text-cyan-600">
                        {data.microbiome_intelligence.precision.title_bg}
                      </summary>
                      <p className="text-[11px] opacity-80 mt-2">
                        {data.microbiome_intelligence.precision.text_bg}
                      </p>
                      <ul className="mt-2 space-y-0.5">
                        {data.microbiome_intelligence.precision.next_bg.map((x, i) => (
                          <li key={i} className="text-[10px] opacity-75">· {x}</li>
                        ))}
                      </ul>
                      <div className="mt-3">
                        <SpecialisedSubModuleSection moduleKey="microbiome" snapshot={data}
                          isDark={isDark} lang={lang} cardCls={cardCls} />
                      </div>
                    </details>
                  )}
                </>
              )}
            </Section>

            {/* ── SECTION 7: Tooth Protection Score ─────────── */}
            <Section tier={tier} icon={Sparkles} title={t.sections.protection} testid="dhi-section-protection">
              {data.tooth_protection?._retired ? (
                <ToothProtectionFactorCard data={data.tooth_protection} lang={lang} isDark={isDark} />
              ) : (
                <ToothProtectionScoreCard
                  data={data.tooth_protection}
                  forecast={data.forecast}
                  isDark={isDark}
                  lang={lang}
                  cardCls={cardCls}
                />
              )}
            </Section>

            {/* ── Флуор и защита на емайла ──────────────────── */}
            <Section tier={tier} icon={Droplets} title={t.sections.fluoride}
              help="Всички източници на флуорид на едно място, оптималният диапазон и границата на безопасност спрямо Вашето тегло."
              testid="dhi-section-fluoride">
              <FluorideIntelligenceCard isDark={isDark} cardCls={cardCls} />
            </Section>

            {/* ── SECTION 8: Systemic-Oral Pathways ─────────── */}
            <Section tier={tier} icon={Activity} title={t.sections.pathways} testid="dhi-section-pathways">
              {data.systemic_pathways?._retired ? (
                <SystemicPathwaysFactorCard data={data.systemic_pathways} lang={lang} isDark={isDark} />
              ) : (
                <SystemicPathwaysCard
                  data={data.systemic_pathways}
                  isDark={isDark}
                  lang={lang}
                  cardCls={cardCls}
                />
              )}
              <div className="mt-3">
                <SystemicSafetyCard isDark={isDark} cardCls={cardCls} />
              </div>
            </Section>
            <Section tier={tier} icon={Sparkles} title={t.sections.dental_age} testid="dhi-section-dental-age">
              {data.dental_age?._retired ? (
                <DentalAgeFactorCard data={data.dental_age} lang={lang} isDark={isDark} />
              ) : (
                <DentalAgeCard
                  data={data.dental_age}
                  forecast={data.forecast}
                  isDark={isDark}
                  lang={lang}
                  cardCls={cardCls}
                />
              )}
            </Section>

            {/* ── SECTION 10: Oral Cancer Prevention ────────── */}
            <Section tier={tier} icon={ShieldAlert} title={t.sections.cancer} testid="dhi-section-cancer">
              <OralCancerPreventionCard
                signals={data.signals}
                isDark={isDark}
                lang={lang}
                cardCls={cardCls}
              />
            </Section>

            {/* ── Самоизследване на устната кухина ──────────── */}
            <Section tier={tier} icon={ShieldAlert} title={t.sections.selfexam}
              help="Воден оглед по последователността на WHO, дневник на лезии със снимки и правилото на трите седмици."
              testid="dhi-section-selfexam">
              <OralSelfExamCard isDark={isDark} cardCls={cardCls} />
              <OralDeficiencySignsCard isDark={isDark} cardCls={cardCls} />
            </Section>

            {/* ── SECTION 11: Intervention Engine ───────────── */}
            <Section tier={tier} icon={Sparkles} title={t.sections.intervention} testid="dhi-section-intervention">
              <InterventionEngineCard
                snapshot={data}
                isDark={isDark}
                lang={lang}
                cardCls={cardCls}
              />
            </Section>

            {/* ── SECTION 12: Interactive Ecosystem Map ─────── */}
            <Section tier={tier} icon={Activity} title={t.sections.map} testid="dhi-section-map">
              <OralEcosystemMap
                ecosystemMap={data.ecosystem_map}
                isDark={isDark}
                lang={lang}
                cardCls={cardCls}
              />
            </Section>

            {/* ── Педиатрична орална превенция (AAP/AAPD) ───── */}
            <Section tier={tier} icon={Sparkles} title={t.sections.pediatric} testid="dhi-section-pediatric">
              <PediatricOralCard isDark={isDark} cardCls={cardCls} />
            </Section>

            {/* ── Силанти на ями и фисури ───────────────────── */}
            <Section tier={tier} icon={Shield} title={t.sections.sealants}
              help="Кои зъби са силанирани, кои чакат и кога е проверката според риска от кариес."
              testid="dhi-section-sealants">
              <SealantTrackerCard isDark={isDark} cardCls={cardCls} childId={childId} />
            </Section>

            {/* ── Техника на четкане ────────────────────────── */}
            <Section tier={tier} icon={Brush} title={t.sections.brushing}
              help="Анимиран урок по Bass и модифициран Bass, таймер по квадранти и проверка след четкане."
              testid="dhi-section-brushing">
              <BrushingTechniqueCard isDark={isDark} cardCls={cardCls} childId={childId} />
            </Section>

            {/* ── Орални навици и лицево развитие ───────────── */}
            <Section tier={tier} icon={Sparkles} title={t.sections.orthodontic} testid="dhi-section-orthodontic">
              <OralHabitsCard isDark={isDark} cardCls={cardCls} />
            </Section>

            {/* ── Физиологичен мост (общ слой между близнаците) ────── */}
            <Section tier={tier} icon={Activity} title={t.sections.physiology}
              help="Обем и осмолалитет, електролити и възбудимост, мембранен транспорт и хомеостазни контури — с посочване кой близнак дърпа всеки сигнал."
              testid="dhi-section-physiology">
              <PhysiologyBridgeCard focus="dental" isDark={isDark} cardCls={cardCls} />
            </Section>

            {/* ── SECTION 15: Implant health (реален бекенд модул) ─── */}
            <Section tier={tier} icon={Sparkles} title={t.sections.implant} testid="dhi-section-implant">
              {data.implant_health?._retired ? (
                <ImplantReadinessFactorCard data={data.implant_health} lang={lang} isDark={isDark} />
              ) : (
                <ImplantHealthCard data={data.implant_health} isDark={isDark} cardCls={cardCls}
                  onSaved={fetchSnapshot} />
              )}
            </Section>

            {/* Bottom CTA — Dental Screening removed per user request — risk assessment is inline (Section 3) */}

            {/* Disclaimers */}
            <div className="mt-6 p-3 rounded-lg text-[11px] opacity-70 text-center"
              style={ord("dhi-disclaimer")}>
              {lang === "en" ? data.disclaimer_en : data.disclaimer_bg}
            </div>

            {/* iter1114.b — Rate-per-feature */}
            <div className="mt-4">
            <ModuleRatingButton moduleKey="dental_intel" moduleName="Зъбния близнак" isDark={isDark} />
            </div>
          </div>
          </DhiCollapseContext.Provider>
          </BhiOrderContext.Provider>
        )}
      </div>
      <SubpageFooter />
    </div>
  );
}

// ─────────────────── helper components ───────────────────

function ZoneDetails({ zone, ecosystem, t, cardCls, lang, isDark }) {
  const z = ecosystem?.[zone];
  if (!z) return null;
  const label = lang === "en" ? z.name_en : z.name_bg;
  // Zone-specific explainer text — describes how each zone score is built
  const ZONE_EXPLAIN = lang === "en" ? {
    enamel:       { inputs: "DIL, daily sugar, meal frequency, smoking, fiber, protein.",          formula: "Starts at 85, subtracts for high DIL, sugar > WHO 25g, frequent eating, smoking; adds for fiber ≥ 25g and adequate protein." },
    dentin:       { inputs: "Vit D, DIL, hs-CRP, magnesium, B12, protein.",                       formula: "Starts at 82, subtracts for Vit D < 30 ng/ml, high DIL, inflammation; adds for Vit D ≥ 50 and adequate protein." },
    periodontium: { inputs: "HbA1c, HOMA-IR, hs-CRP, smoking, stress, omega-3, fiber.",            formula: "Starts at 80, big penalties for HbA1c ≥ 6.5%, smoking, hs-CRP > 3; bonuses for omega-3 ≥ 8% and fiber ≥ 30g." },
    microbiome:   { inputs: "DIL, daily sugar, meal frequency, smoking, alcohol, fiber.",          formula: "Starts at 78, subtracts for cariogenic load (high DIL/sugar/snacking) and dysbiosis drivers (smoking, alcohol); adds for high fiber and polyphenol-rich diet." },
    saliva:       { inputs: "Smoking, alcohol, stress, sleep, protein, fiber.",                    formula: "Starts at 82, subtracts for xerostomia drivers (smoking, alcohol, stress, poor sleep); adds for chewing-stimulating fiber and protein." },
  } : {
    enamel:       { inputs: "DIL, дневна захар, честота на хранене, тютюнопушене, фибри, протеин.", formula: "Започва от 85; намалява при висок DIL, захар > WHO 25g, често хапване, пушене; добавки за фибри ≥ 25g и адекватен протеин." },
    dentin:       { inputs: "Vit D, DIL, hs-CRP, магнезий, B12, протеин.",                          formula: "Започва от 82; намалява при Vit D < 30 ng/ml, висок DIL, възпаление; добавки за Vit D ≥ 50 и адекватен протеин." },
    periodontium: { inputs: "HbA1c, HOMA-IR, hs-CRP, тютюнопушене, стрес, омега-3, фибри.",         formula: "Започва от 80; големи отстъпки при HbA1c ≥ 6.5%, пушене, hs-CRP > 3; бонуси за омега-3 ≥ 8% и фибри ≥ 30g." },
    microbiome:   { inputs: "DIL, дневна захар, честота на хранене, тютюнопушене, алкохол, фибри.", formula: "Започва от 78; намалява при кариогенен товар (висок DIL/захар/закуски) и дисбиоза (пушене, алкохол); добавки за високи фибри и полифенолна диета." },
    saliva:       { inputs: "Тютюнопушене, алкохол, стрес, сън, протеин, фибри.",                   formula: "Започва от 82; намалява при ксеростомия (пушене, алкохол, стрес, лош сън); добавки за дъвчене на фибри и адекватен протеин." },
  };
  const expl = ZONE_EXPLAIN[zone];
  return (
    <motion.div
      key={zone}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={`${cardCls} p-5`}
      data-testid={`dhi-zone-details-${zone}`}
    >
      <div className="flex items-center justify-between mb-3">
        <h3 className="text-lg font-bold">{label}</h3>
        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${BAND_PILL[z.band]}`} data-testid={`dhi-zone-band-${zone}`}>
          {t.statusBands[z.band]} · {z.score}/100
        </span>
      </div>
      <div className="text-xs opacity-70 mb-2">{t.factors}:</div>
      <ul className="space-y-1.5 text-sm">
        {z.factors && z.factors.length ? z.factors.map((f, i) => (
          <li key={i} className="flex items-start gap-2">
            <span className="text-cyan-500 mt-1">•</span>
            <span>{f}</span>
          </li>
        )) : (
          <li className="opacity-50 italic text-xs">
            {lang === "en" ? "No specific risk factors detected at the moment." : lang === "es" ? "Sin factores específicos detectados." : "Без специфични фактори към момента."}
          </li>
        )}
      </ul>
      {expl && (
        <ScoreExplanation
          isDark={isDark}
          lang={lang}
          testid={`dhi-zone-why-${zone}`}
          inputs={expl.inputs}
          formula={expl.formula}
          factors={z.factors || []}
        />
      )}
    </motion.div>
  );
}

const DHI_ESSENTIAL = new Set([
  "dhi-section-risk", "dhi-section-plan", "dhi-section-ecosystem",
  "dhi-section-metabolic", "dhi-section-caries", "dhi-section-periodontal",
  "dhi-section-indices", "dhi-section-selfexam", "dhi-section-brushing",
]);

/** iter1029 — сгъваеми секции: състоянието се помни в localStorage. */
const COLLAPSE_KEY = "dhi:collapsed";
export const DhiCollapseContext = React.createContext(null);

export function useDhiCollapse() {
  const [collapsed, setCollapsed] = useState(() => {
    try { return JSON.parse(localStorage.getItem(COLLAPSE_KEY) || "{}"); }
    catch { return {}; }
  });
  const persist = useCallback((next) => {
    setCollapsed(next);
    try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(next)); }
    catch { /* частен режим */ }
  }, []);
  const toggle = useCallback((key) => {
    persist({ ...collapsed, [key]: !collapsed[key] });
  }, [collapsed, persist]);
  const expand = useCallback((key) => {
    if (collapsed[key]) persist({ ...collapsed, [key]: false });
  }, [collapsed, persist]);
  const collapseAll = useCallback((keys) => {
    persist(Object.fromEntries(keys.map((k) => [k, true])));
  }, [persist]);
  const expandAll = useCallback(() => persist({}), [persist]);
  return { collapsed, toggle, expand, collapseAll, expandAll };
}

function Section({ icon: Icon, title, help, children, testid, tier = "advanced" }) {
  const orderStyle = useBhiOrderStyle(testid);
  const ctx = React.useContext(DhiCollapseContext);
  if (tier === "essential" && !DHI_ESSENTIAL.has(testid)) return null;
  const isCollapsed = !!ctx?.collapsed?.[testid];
  return (
    <section className="mb-8 scroll-mt-20" data-testid={testid} id={testid}
      style={orderStyle}>
      <button type="button"
        onClick={() => ctx?.toggle(testid)}
        className="w-full flex items-center gap-2 mb-1 text-left group"
        aria-expanded={!isCollapsed}
        data-testid={`${testid}-toggle`}>
        <Icon className="w-5 h-5 text-cyan-500 shrink-0" />
        <h2 className="text-lg sm:text-xl font-bold min-w-0">{title}</h2>
        <ChevronDown className={`w-4 h-4 ml-auto shrink-0 opacity-50 transition-transform group-hover:opacity-100 ${
          isCollapsed ? "-rotate-90" : ""}`} />
      </button>
      {!isCollapsed && help && <p className="text-xs opacity-60 mb-3">{help}</p>}
      {!isCollapsed && children}
    </section>
  );
}

function StatTile({ label, value, testid, computeRoute, computeLabel, lang = "bg", isDark = false }) {
  const isMissing = value === null || value === undefined || value === "—";
  return (
    <div
      className="p-3 rounded-xl bg-gradient-to-br from-cyan-500/10 to-sky-500/10 border border-cyan-200/30 dark:border-cyan-500/20"
      data-testid={testid}
    >
      <div className="text-[11px] opacity-60 mb-0.5">{label}</div>
      <div className="text-2xl font-bold">{value}</div>
      {isMissing && computeRoute && (
        <div className="mt-1">
          <ComputeIndexCTA
            value={value}
            route={computeRoute}
            label_bg={computeLabel}
            label_en={computeLabel}
            lang={lang}
            isDark={isDark}
            size="xs"
            testid={`${testid}-compute`}
          />
        </div>
      )}
    </div>
  );
}

function ScoreRing({ score, band }) {
  const radius = 60;
  const stroke = 12;
  const circ = 2 * Math.PI * radius;
  const dash = (score / 100) * circ;
  const colors = {
    green:  "#22C55E",
    yellow: "#EAB308",
    orange: "#F97316",
    red:    "#EF4444",
  };
  return (
    <div className="relative" style={{ width: 160, height: 160 }} data-testid="dhi-metabolic-score-ring">
      <svg width="160" height="160" viewBox="0 0 160 160">
        <circle cx="80" cy="80" r={radius} stroke="rgba(0,0,0,0.06)" strokeWidth={stroke} fill="none" />
        <motion.circle
          cx="80" cy="80" r={radius}
          stroke={colors[band] || "#EAB308"}
          strokeWidth={stroke}
          strokeLinecap="round"
          fill="none"
          strokeDasharray={`${dash} ${circ - dash}`}
          transform="rotate(-90 80 80)"
          initial={{ strokeDasharray: `0 ${circ}` }}
          animate={{ strokeDasharray: `${dash} ${circ - dash}` }}
          transition={{ duration: 1.2, ease: "easeOut" }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <div className="text-4xl font-extrabold">{score}</div>
        <div className="text-[10px] opacity-60">/ 100</div>
      </div>
    </div>
  );
}

function SubScoreCard({ name, score, band, lang }) {
  const labels = {
    bg: {
      sugar: "Захар",
      acid: "Киселина",
      inflammatory: "Възпаление",
      insulin: "Инсулин",
      protective: "Защита",
    },
    en: {
      sugar: "Sugar",
      acid: "Acid",
      inflammatory: "Inflammation",
      insulin: "Insulin",
      protective: "Protection",
    },
    es: {
      sugar: "Azúcar",
      acid: "Ácido",
      inflammatory: "Inflamación",
      insulin: "Insulina",
      protective: "Protección",
    },
  };
  return (
    <div
      className={`p-3 rounded-xl text-center ${BAND_PILL[band]}`}
      data-testid={`dhi-sub-${name}`}
    >
      <div className="text-[10px] opacity-80 mb-0.5">{labels[lang]?.[name] || labels.bg[name] || name}</div>
      <div className="text-xl font-bold">{score}</div>
    </div>
  );
}

